import { partytownSnippet } from '@qwik.dev/partytown/integration';

type GoogleWindow = Window & {
  w369AnalyticsLayer: unknown[];
  __w369WorkerGtag?: (...args: unknown[]) => void;
};
const callbacks = new Map<number, (...args: unknown[]) => void>();
let nextCallbackId = 0;
const mapValues = (value: any, transform: (value: any) => any): any => {
  const changed = transform(value);
  if (changed !== value) return changed;
  if (Array.isArray(value)) return value.map(item => mapValues(item, transform));
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, mapValues(item, transform)]));
  }
  return value;
};

// Partytown drops function values. Keep callbacks on main and send an ID so the
// worker can notify their owner; native fallback receives the original function.
export function sendGoogleTagToWorker(args: IArguments) {
  const w = window as GoogleWindow;
  if (!w.__w369WorkerGtag) return false;
  const encoded = mapValues(Array.from(args), value => {
    if (typeof value !== 'function') return value;
    const id = ++nextCallbackId;
    callbacks.set(id, (...values) => { callbacks.delete(id); value(...values); });
    return { __w369GoogleCallback: id };
  });
  w.__w369WorkerGtag(...encoded);
  return true;
}

// Only consented GA4 runs in the worker; Ads uses its normal loader. Attribution redirect hops use
// the regular loader because they leave before a worker may finish starting.
export function injectGoogleTag(id: string, useWorker: boolean) {
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}&l=w369AnalyticsLayer`;
  script.async = true;
  if (!useWorker || !window.isSecureContext || !navigator.serviceWorker) {
    document.head.appendChild(script);
    return;
  }

  const w = window as GoogleWindow;
  const pending = w.w369AnalyticsLayer.map((command: any) =>
    typeof command?.length === 'number' ? Array.from(command) : command,
  );
  window.addEventListener('message', event => {
    // Partytown calls postMessage from its same-origin sandbox iframe.
    if (event.origin !== window.location.origin) return;
    const id = event.data?.__w369GoogleCallback;
    if (typeof id === 'number' && Array.isArray(event.data.args)) callbacks.get(id)?.(...event.data.args);
  });
  w.__w369WorkerGtag = function () {
    const decoded = mapValues(Array.from(arguments), value =>
      value?.__w369GoogleCallback ? callbacks.get(value.__w369GoogleCallback) : value,
    );
    (function () { w.w369AnalyticsLayer.push(arguments); }).apply(window, decoded);
  };
  const bootstrap = document.createElement('script');
  bootstrap.type = 'text/partytown';
  // Forward function arguments, not dataLayer's Arguments objects: serializing
  // those objects strips their type, and Google silently ignores the commands.
  // The first consent/js commands precede worker setup; restore their Date too.
  // On fallback Partytown removes only the bridge. The real dataLayer already
  // holds every command, so this initializer must not replay them on main.
  bootstrap.textContent = `if (document.currentScript.type.startsWith('text/partytown')) {
    // Nuxt exposes global as a Window alias. Bind it locally before Google's
    // global-object detection: Partytown otherwise constructs a second Window
    // for that alias and loses the worker's initialized command bridge.
    window.global = window;
    window.w369AnalyticsLayer = [];
    const decode = function (value) {
      if (value && value.__w369GoogleCallback) return function () {
        window.postMessage({ __w369GoogleCallback: value.__w369GoogleCallback, args: Array.from(arguments) }, window.location.origin);
      };
      if (Array.isArray(value)) return value.map(decode);
      if (value && Object.getPrototypeOf(value) === Object.prototype) return Object.fromEntries(Object.entries(value).map(function (entry) { return [entry[0], decode(entry[1])]; }));
      return value;
    };
    window.__w369WorkerGtag = window.gtag = function () {
      (function () { window.w369AnalyticsLayer.push(arguments); }).apply(window, Array.from(arguments).map(decode));
    };
    ${JSON.stringify(pending)}.forEach(function (command) {
      if (Array.isArray(command)) {
        if (command[0] === 'js') command[1] = new Date(command[1]);
        window.__w369WorkerGtag.apply(window, command);
      } else { window.w369AnalyticsLayer.push(command); }
    });
  }`;
  document.head.appendChild(bootstrap);
  script.type = 'text/partytown';
  document.head.appendChild(script);

  const runtime = document.createElement('script');
  runtime.textContent = partytownSnippet({
    lib: '/~partytown/v1/',
    forward: [['__w369WorkerGtag', { preserveBehavior: true }]],
    fallbackTimeout: 2500,
  });
  document.head.appendChild(runtime);
}
